/**
 * Which lane (old PlayChess / new PlayChessV2) serves this tab.
 *
 * Order, first match wins — for a user the cohort lets in (`canSwitchLane`):
 *   1. `sessionStorage.cc_play_lane` — the lock this tab already took;
 *   2. `localStorage.play_v2` — per-user override ('1' new, '0' old);
 *   3. `ENV.PLAY_V2_COHORT` — 'off' | 'staff' | 'flag' | 'all' (rollout gate);
 *   4. 'old'.
 * A user the cohort does NOT let in gets 'old' unconditionally — no lock, no
 * override, no badge — so a stray storage value can never show V2 to someone
 * outside the rollout.
 *
 * Fix AE (owner 2026-10-07, "push V2 behind the feature flag for the users I
 * name"): the `'flag'` cohort reads the per-user flags the profile call
 * returns (`learning/get_profile` → `user_flags: {flag_key: flag_value}`,
 * rows of `cc_user_flags` keyed by `cc_control_flags.flag_key`). The key is
 * `play_v2`; value 1 lets the user in — and nothing else does, staff included
 * (the team enrols itself with a flag row like any other user).
 *
 * Deliberately SYNCHRONOUS: with the cohort off and no override this returns
 * 'old' before the first render, so /playChess is byte-identical to today.
 * Web storage is accessed directly (the `@/Utils/CommonUtils` helpers are
 * localForage-backed and async) behind try/catch + a `window` guard, because
 * this module is imported by the react-native build too.
 */
import ENV from '../../../environment';

export type Lane = 'old' | 'new';

export const LANE_SESSION_KEY = 'cc_play_lane';
export const LANE_OVERRIDE_KEY = 'play_v2';

const readSession = (key: string): string | null => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const readLocal = (key: string): string | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

/** the `cc_control_flags.flag_key` that opens the V2 lane (fix AE) */
export const PLAY_V2_FLAG_KEY = 'play_v2';

export type UserFlags = {[flagKey: string]: unknown} | null | undefined;

export type LaneAudience = {isStaff?: boolean; userFlags?: UserFlags};

/** the profile's `user_flags.play_v2` is 1 (an int column; tolerate '1'/true) */
export function hasPlayV2Flag(userFlags?: UserFlags): boolean {
  const value = userFlags ? userFlags[PLAY_V2_FLAG_KEY] : undefined;
  return value === 1 || value === '1' || value === true;
}

/**
 * Is this user inside the rollout at all? Decides BOTH whether the lock /
 * override are honoured and whether the lane badge is shown.
 */
export function canSwitchLane({
  isStaff,
  userFlags,
}: LaneAudience = {}): boolean {
  const cohort = (ENV as any)?.PLAY_V2_COHORT;
  if (cohort === 'all') {
    return true;
  }
  if (cohort === 'staff') {
    return !!isStaff;
  }
  if (cohort === 'flag') {
    // flag ONLY — staff get a `cc_user_flags` row like everyone else (owner,
    // 2026-10-07: "we are relying on the feature flag")
    return hasPlayV2Flag(userFlags);
  }
  return false;
}

export function resolveLane(audience: LaneAudience = {}): Lane {
  if (!canSwitchLane(audience)) {
    return 'old';
  }
  const locked = readSession(LANE_SESSION_KEY);
  if (locked === 'new' || locked === 'old') {
    return locked;
  }

  const override = readLocal(LANE_OVERRIDE_KEY);
  if (override === '1') {
    return 'new';
  }
  if (override === '0') {
    return 'old';
  }

  // inside the rollout and nothing pinned: the cohort's own default is NEW
  // ('all', a staff member under 'staff', a flagged user or staff under
  // 'flag') — the badge is how they go back to the old screen.
  return 'new';
}

/** Pin the tab to a lane; handlers registered by one lane cannot be removed. */
export function lockLane(lane: Lane): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }
    window.sessionStorage.setItem(LANE_SESSION_KEY, lane);
  } catch (e) {
    // sessionStorage can throw in private mode — the lane simply is not sticky
  }
}

/** Per-user override ('1' new / '0' old); null clears it. */
export function setLaneOverride(lane: Lane | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    if (lane === null) {
      window.localStorage.removeItem(LANE_OVERRIDE_KEY);
    } else {
      window.localStorage.setItem(
        LANE_OVERRIDE_KEY,
        lane === 'new' ? '1' : '0',
      );
    }
  } catch (e) {
    // storage unavailable — nothing to persist
  }
}
